import React, { useState, useEffect } from 'react';
import NotificationManager from './NotificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function NotificationSettings({ token, onClose }) {
  const [activeTab, setActiveTab] = useState('reports');
  const [reportSettings, setReportSettings] = useState(null);
  const [telegramSettings, setTelegramSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [showNotificationManager, setShowNotificationManager] = useState(false);
  const [botStatus, setBotStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);

  // Is the bot working, and are its messages (the "/start CODE" for My Telegram) reaching THIS app?
  const fetchBotStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings/telegram/status`, { headers: { 'Authorization': `Bearer ${token}` } });
      setBotStatus(res.ok ? await res.json() : null);
    } catch (err) { setBotStatus(null); }
  };

  useEffect(() => { if (activeTab === 'telegram') fetchBotStatus(); }, [activeTab]);

  const connectBot = async () => {
    if (!window.confirm(
      `Point @${botStatus.bot_username} at THIS app?\n\n` +
      'A bot can only send to one app. If the same bot is used by another copy of the app (preview or live), ' +
      '"My Telegram" linking will stop working there. Only do this for the bot that belongs to this app.')) return;
    setConnecting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings/telegram/connect`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      setMessage(data.ok ? { type: 'success', text: `@${data.bot_username} is now connected to this app.` }
                         : { type: 'error', text: `Could not connect the bot: ${data.message || data.detail || res.status}` });
      fetchBotStatus();
    } catch (err) { setMessage({ type: 'error', text: 'Could not connect the bot' }); }
    setConnecting(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [reportRes, telegramRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/settings/scheduled-reports`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/settings/telegram`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (reportRes.ok) setReportSettings(await reportRes.json());
      if (telegramRes.ok) setTelegramSettings(await telegramRes.json());
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    }
    setLoading(false);
  };

  const saveReportSettings = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('enabled', reportSettings.enabled);
      formData.append('schedule_type', reportSettings.schedule_type);
      formData.append('send_time', reportSettings.send_time);
      formData.append('send_day', reportSettings.send_day);
      formData.append('recipients', reportSettings.recipients.join(','));
      formData.append('include_vehicles_down', reportSettings.include_vehicles_down);
      formData.append('include_pending_approvals', reportSettings.include_pending_approvals);
      formData.append('include_servicing_due', reportSettings.include_servicing_due);
      formData.append('include_weekly_repairs', reportSettings.include_weekly_repairs);
      formData.append('include_cost_summary', reportSettings.include_cost_summary);

      const response = await fetch(`${BACKEND_URL}/api/settings/scheduled-reports`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Report settings saved!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }
    setSaving(false);
  };

  const saveTelegramSettings = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('enabled', telegramSettings.enabled);
      formData.append('bot_token', telegramSettings.bot_token || '');
      formData.append('chat_id', telegramSettings.chat_id);
      formData.append('notify_pending_approvals', telegramSettings.notify_pending_approvals);
      formData.append('notify_servicing_due', telegramSettings.notify_servicing_due);
      formData.append('notify_more_info_requests', telegramSettings.notify_more_info_requests);
      formData.append('notify_high_cost_repairs', telegramSettings.notify_high_cost_repairs);

      const response = await fetch(`${BACKEND_URL}/api/settings/telegram`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const conn = data.connection;
        if (conn && !conn.ok) {
          setMessage({ type: 'error', text: data.message });
        } else if (conn && conn.ok) {
          setMessage({ type: 'success', text: `Telegram settings saved – bot @${conn.bot_username} is connected, so "My Telegram" linking will work.` });
        } else {
          setMessage({ type: 'success', text: 'Telegram settings saved!' });
        }
        // Refresh to get masked token
        fetchSettings();
        fetchBotStatus();
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }
    setSaving(false);
  };

  const testTelegram = async () => {
    setTestingTelegram(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/telegram/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: '✅ Test message sent successfully!' });
      } else {
        setMessage({ type: 'error', text: data.detail || data.message || 'Failed to send test' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send test message' });
    }
    setTestingTelegram(false);
  };

  const sendPendingApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/telegram/send-pending-approvals`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMessage({ type: response.ok ? 'success' : 'error', text: data.message || data.detail });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send notifications' });
    }
  };

  const sendServicingDue = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/telegram/send-servicing-due`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMessage({ type: response.ok ? 'success' : 'error', text: data.message || data.detail });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send notifications' });
    }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading settings...</div>;

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    fontSize: '1rem',
    background: 'white'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#1E293B',
    fontSize: '0.875rem'
  };

  const sectionStyle = {
    background: '#F8FAFC',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem'
  };

  const checkboxStyle = {
    width: '18px',
    height: '18px',
    marginRight: '0.75rem',
    cursor: 'pointer'
  };

  const tabStyle = (isActive) => ({
    padding: '0.75rem 1.5rem',
    background: isActive ? 'white' : 'transparent',
    border: 'none',
    borderBottom: isActive ? '3px solid #003366' : '3px solid transparent',
    cursor: 'pointer',
    fontWeight: isActive ? '700' : '500',
    color: isActive ? '#003366' : '#64748B',
    fontSize: '0.95rem'
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '16px 16px 0 0',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📬 Notification Settings</h2>
            <button 
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1.25rem'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #E2E8F0',
          background: '#F8FAFC'
        }}>
          <button style={tabStyle(activeTab === 'reports')} onClick={() => setActiveTab('reports')}>
            📧 Email Reports
          </button>
          <button style={tabStyle(activeTab === 'telegram')} onClick={() => setActiveTab('telegram')}>
            📱 Telegram
          </button>
          <button style={tabStyle(activeTab === 'routing')} onClick={() => setActiveTab('routing')}>
            🔔 Routing Rules
          </button>
        </div>

        {message && (
          <div style={{
            padding: '1rem',
            margin: '1rem',
            borderRadius: '8px',
            background: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
            color: message.type === 'success' ? '#065F46' : '#991B1B'
          }}>
            {message.text}
          </div>
        )}

        {/* Email Reports Tab */}
        {activeTab === 'reports' && reportSettings && (
          <div style={{ padding: '1.5rem' }}>
            {/* Enable Toggle */}
            <div style={sectionStyle}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  id="reports_enabled"
                  checked={reportSettings.enabled}
                  onChange={(e) => setReportSettings({...reportSettings, enabled: e.target.checked})}
                  style={checkboxStyle}
                />
                <label htmlFor="reports_enabled" style={{ fontWeight: '700', color: '#003366', fontSize: '1rem', cursor: 'pointer' }}>
                  Enable Scheduled Reports
                </label>
              </div>
            </div>

            {reportSettings.enabled && (
              <>
                {/* Schedule Settings */}
                <div style={sectionStyle}>
                  <h4 style={{ margin: '0 0 1rem', color: '#003366' }}>📅 Schedule</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Frequency</label>
                      <select
                        value={reportSettings.schedule_type}
                        onChange={(e) => setReportSettings({...reportSettings, schedule_type: e.target.value})}
                        style={inputStyle}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                    {reportSettings.schedule_type === 'weekly' && (
                      <div>
                        <label style={labelStyle}>Day</label>
                        <select
                          value={reportSettings.send_day}
                          onChange={(e) => setReportSettings({...reportSettings, send_day: e.target.value})}
                          style={inputStyle}
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <label style={labelStyle}>Time</label>
                      <input
                        type="time"
                        value={reportSettings.send_time}
                        onChange={(e) => setReportSettings({...reportSettings, send_time: e.target.value})}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                {/* Recipients */}
                <div style={sectionStyle}>
                  <h4 style={{ margin: '0 0 1rem', color: '#003366' }}>👥 Recipients</h4>
                  <label style={labelStyle}>Email Addresses (comma-separated)</label>
                  <textarea
                    value={reportSettings.recipients.join(', ')}
                    onChange={(e) => setReportSettings({
                      ...reportSettings, 
                      recipients: e.target.value.split(',').map(r => r.trim()).filter(r => r)
                    })}
                    placeholder="email1@example.com, email2@example.com"
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}}
                  />
                </div>

                {/* Report Items */}
                <div style={sectionStyle}>
                  <h4 style={{ margin: '0 0 1rem', color: '#003366' }}>📋 Include in Report</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    {[
                      { key: 'include_vehicles_down', label: '🔧 Vehicles Down' },
                      { key: 'include_pending_approvals', label: '⏳ Pending Approvals' },
                      { key: 'include_servicing_due', label: '🛠️ Servicing Due' },
                      { key: 'include_weekly_repairs', label: '📊 Weekly Repairs' },
                      { key: 'include_cost_summary', label: '💰 Cost Summary' }
                    ].map(item => (
                      <div key={item.key} style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          id={item.key}
                          checked={reportSettings[item.key]}
                          onChange={(e) => setReportSettings({...reportSettings, [item.key]: e.target.checked})}
                          style={checkboxStyle}
                        />
                        <label htmlFor={item.key} style={{ cursor: 'pointer' }}>{item.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <button
              onClick={saveReportSettings}
              disabled={saving}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                color: 'white',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Report Settings'}
            </button>
          </div>
        )}

        {/* Telegram Tab */}
        {activeTab === 'telegram' && telegramSettings && (
          <div style={{ padding: '1.5rem' }}>
            {/* Enable Toggle */}
            <div style={sectionStyle}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  id="telegram_enabled"
                  checked={telegramSettings.enabled}
                  onChange={(e) => setTelegramSettings({...telegramSettings, enabled: e.target.checked})}
                  style={checkboxStyle}
                />
                <label htmlFor="telegram_enabled" style={{ fontWeight: '700', color: '#003366', fontSize: '1rem', cursor: 'pointer' }}>
                  Enable Telegram Notifications
                </label>
              </div>
            </div>

            {telegramSettings.enabled && (
              <>
                {/* Bot Configuration */}
                <div style={sectionStyle}>
                  <h4 style={{ margin: '0 0 1rem', color: '#003366' }}>🤖 Bot Configuration</h4>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={labelStyle}>
                      Bot Token 
                      {telegramSettings.bot_token_masked && (
                        <span style={{ fontWeight: '400', color: '#059669', marginLeft: '0.5rem' }}>
                          (configured: {telegramSettings.bot_token_masked})
                        </span>
                      )}
                    </label>
                    <input
                      type="password"
                      value={telegramSettings.bot_token || ''}
                      onChange={(e) => setTelegramSettings({...telegramSettings, bot_token: e.target.value})}
                      placeholder={telegramSettings.bot_token_masked ? 'Leave empty to keep current' : 'Enter bot token from @BotFather'}
                      style={inputStyle}
                    />
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#64748B' }}>
                      Get your bot token from <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer">@BotFather</a> on Telegram.
                      Use a separate bot for the preview app – a bot can only send to one app.
                    </p>
                  </div>
                  {botStatus && (
                    <div data-testid="telegram-bot-status" style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '8px', fontSize: '0.85rem',
                      background: botStatus.bot_ok && botStatus.points_here ? '#ECFDF5' : '#FFFBEB',
                      border: `1px solid ${botStatus.bot_ok && botStatus.points_here ? '#A7F3D0' : '#FDE68A'}` }}>
                      <strong>"My Telegram" linking: </strong>
                      {!botStatus.bot_ok ? <>the bot is not working ({botStatus.bot_error}).</>
                        : botStatus.points_here ? <>✅ @{botStatus.bot_username} is connected to this app.</>
                        : <>⚠️ @{botStatus.bot_username}'s messages go to {botStatus.webhook_url ? <code>{botStatus.webhook_url}</code> : 'nowhere'}, not this app, so linking won't work here.</>}
                      {botStatus.last_error && <div style={{ marginTop: '0.25rem', color: '#92400E' }}>Last problem reported by Telegram: {botStatus.last_error}</div>}
                      <div style={{ marginTop: '0.25rem', color: '#64748B' }}>{botStatus.linked_users} user(s) linked.</div>
                      {botStatus.bot_ok && !botStatus.points_here && (
                        <button onClick={connectBot} disabled={connecting} data-testid="telegram-connect-btn"
                          style={{ marginTop: '0.5rem', padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid #D97706', background: 'white', cursor: 'pointer' }}>
                          {connecting ? 'Connecting…' : 'Connect this bot to this app'}
                        </button>
                      )}
                    </div>
                  )}
                  <div>
                    <label style={labelStyle}>Chat ID (group or individual)</label>
                    <input
                      type="text"
                      value={telegramSettings.chat_id || ''}
                      onChange={(e) => setTelegramSettings({...telegramSettings, chat_id: e.target.value})}
                      placeholder="e.g., -1001234567890 for groups"
                      style={inputStyle}
                    />
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#64748B' }}>
                      For groups, add bot to group and use @userinfobot to get the chat ID
                    </p>
                  </div>
                </div>

                {/* Notification Types */}
                <div style={sectionStyle}>
                  <h4 style={{ margin: '0 0 1rem', color: '#003366' }}>🔔 Notification Types</h4>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {[
                      { key: 'notify_pending_approvals', label: '⏳ Pending Approvals', desc: 'New repair requests needing approval' },
                      { key: 'notify_servicing_due', label: '🛠️ Servicing Due', desc: 'Vehicles due for scheduled service' },
                      { key: 'notify_more_info_requests', label: '📋 More Info Requests', desc: 'Requests for additional information' },
                      { key: 'notify_high_cost_repairs', label: '💰 High Cost Repairs', desc: 'Repairs exceeding cost threshold' }
                    ].map(item => (
                      <div key={item.key} style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <input
                          type="checkbox"
                          id={item.key}
                          checked={telegramSettings[item.key]}
                          onChange={(e) => setTelegramSettings({...telegramSettings, [item.key]: e.target.checked})}
                          style={{...checkboxStyle, marginTop: '2px'}}
                        />
                        <label htmlFor={item.key} style={{ cursor: 'pointer' }}>
                          <span style={{ fontWeight: '600' }}>{item.label}</span>
                          <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748B' }}>{item.desc}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Test & Manual Send */}
                <div style={sectionStyle}>
                  <h4 style={{ margin: '0 0 1rem', color: '#003366' }}>🧪 Test & Send</h4>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={testTelegram}
                      disabled={testingTelegram || !telegramSettings.chat_id}
                      style={{
                        padding: '0.625rem 1rem',
                        background: '#059669',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: testingTelegram ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        color: 'white',
                        opacity: testingTelegram ? 0.7 : 1
                      }}
                    >
                      {testingTelegram ? 'Sending...' : '✅ Test Connection'}
                    </button>
                    <button
                      onClick={sendPendingApprovals}
                      disabled={!telegramSettings.enabled}
                      style={{
                        padding: '0.625rem 1rem',
                        background: '#D97706',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        color: 'white',
                        opacity: telegramSettings.enabled ? 1 : 0.5
                      }}
                    >
                      📤 Send Pending Approvals
                    </button>
                    <button
                      onClick={sendServicingDue}
                      disabled={!telegramSettings.enabled}
                      style={{
                        padding: '0.625rem 1rem',
                        background: '#0284C7',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        color: 'white',
                        opacity: telegramSettings.enabled ? 1 : 0.5
                      }}
                    >
                      📤 Send Servicing Due
                    </button>
                  </div>
                </div>
              </>
            )}

            <button
              onClick={saveTelegramSettings}
              disabled={saving}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                color: 'white',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Telegram Settings'}
            </button>
          </div>
        )}

        {/* Routing Rules Tab */}
        {activeTab === 'routing' && (
          <div style={{ padding: '1.5rem' }}>
            <div style={{
              background: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: '12px',
              padding: '1.25rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#1E40AF' }}>🔔 Role-Based Notification Routing</h4>
              <p style={{ margin: 0, color: '#1E40AF', fontSize: '0.9rem' }}>
                Configure which roles and users receive each type of notification. 
                Set thresholds, delivery channels, and department-based routing.
              </p>
            </div>
            
            <button
              onClick={() => setShowNotificationManager(true)}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                color: 'white',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              🔔 Open Notification Manager
            </button>
            
            <div style={{ marginTop: '1.5rem', color: '#64748B', fontSize: '0.85rem' }}>
              <p style={{ margin: '0 0 0.5rem' }}><strong>Available Roles:</strong></p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {['Staff', 'Supervisor', 'Manager', 'Finance', 'Senior Manager', 'CEO', 'Super Admin'].map(role => (
                  <span key={role} style={{
                    background: '#F1F5F9',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '100px',
                    fontSize: '0.8rem'
                  }}>
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Notification Manager Modal */}
        {showNotificationManager && (
          <NotificationManager 
            token={token} 
            onClose={() => setShowNotificationManager(false)} 
          />
        )}
      </div>
    </div>
  );
}

export default NotificationSettings;
